import React, { createContext, useContext, useState, useEffect } from 'react'

const ChatbotContext = createContext({})

export const useChatbot = () => {
  const context = useContext(ChatbotContext)
  if (!context) {
    throw new Error('useChatbot must be used within a ChatbotProvider')
  }
  return context
}

export const ChatbotProvider = ({ children, config }) => {
  const [isEnabled, setIsEnabled] = useState(false)
  const [chatbotConfig, setChatbotConfig] = useState({
    name: 'Assistant',
    welcomeMessage: 'Hello! How can I help you today?',
    position: 'bottom-right',
    primaryColor: '#2563eb',
    features: ['general-support', 'faq-answers'],
    customInstructions: '',
    knowledgeBase: '',
    maxTokens: 150,
    temperature: 0.7,
    ...config
  })

  useEffect(() => {
    // Check if chatbot should be enabled based on current prompt data
    const checkChatbotStatus = () => {
      const savedPromptData = localStorage.getItem('current-prompt-data')
      if (savedPromptData) {
        try {
          const promptData = JSON.parse(savedPromptData)
          if (promptData.includeAiChatbot) {
            setIsEnabled(true)
            setChatbotConfig(prev => ({
              ...prev,
              ...promptData.chatbotConfig
            }))
          } else {
            setIsEnabled(false)
          }
        } catch (error) {
          console.error('Error parsing prompt data:', error)
        }
      }
    }

    checkChatbotStatus()

    // Listen for changes to prompt data
    const handleStorageChange = () => {
      checkChatbotStatus()
    }

    window.addEventListener('storage', handleStorageChange)
    
    // Custom event for same-tab updates
    window.addEventListener('promptDataChanged', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('promptDataChanged', handleStorageChange)
    }
  }, [])

  const updateChatbotConfig = (newConfig) => {
    setChatbotConfig(prev => ({
      ...prev,
      ...newConfig
    }))
  }

  const enableChatbot = (config) => {
    setIsEnabled(true)
    if (config) {
      updateChatbotConfig(config)
    }
  }

  const disableChatbot = () => {
    setIsEnabled(false)
  }

  const value = {
    isEnabled,
    chatbotConfig,
    updateChatbotConfig,
    enableChatbot,
    disableChatbot
  }

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  )
}